import { Model, StackProjections } from 'jms-domain'
import { BottomWallItem } from 'jms-domain/src/__generated/model/index.js'

import type { DomainEntityModification } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'
import { generateCountertopRepositioningUpdates } from './utils/countertop-repositioning.js'

/**
 * Fill empty space after wall item deleted.
 */
export const fillEmptySpaceAfterWallItemDeleteTransformer = configuratorInterface.transformerBuilder.when
  .update(['deleteEntity'])
  .fired.and(ctx => BottomWallItem.isId(ctx.update.entityId))
  .then.applySideEffect(ctx => {
    const entityId = ctx.update.entityId as Model.BottomWallItem['id']
    const oldState = ctx.getOldState()
    const newState = ctx.getNewState()

    const $oldEntity = ctx.domainContext.domain.get$Entity(entityId, oldState)
    const $wall = ctx.domainContext.domain.get$Entity($oldEntity.wallId, newState)
    const isLeftDirection = $wall.$direction === Model.WallDirection.left
    const sortedWallItems = [...$wall.$bottomItems].sort((a, b) => (isLeftDirection ? b.x - a.x : a.x - b.x))

    const bottomItemUpdates: Array<DomainEntityModification<Model.AnyDomainEntity>> = []

    let nextItemOffset = $wall.$direction === Model.WallDirection.left ? $oldEntity.x + $oldEntity.$width : $oldEntity.x

    // Track new positions of bottom items for countertop repositioning
    const newBottomItemPositions: Array<{ id: string; x: number; $width: number; $hasWorkSurface: boolean }> = []

    for (const item of sortedWallItems) {
      // Don't move items that were positioned before the deleted item.
      const shouldSkip = $wall.$direction === Model.WallDirection.left ? item.x > $oldEntity.x : item.x < $oldEntity.x
      if (shouldSkip) {
        // Keep items that aren't moving in their current positions
        newBottomItemPositions.push({
          id: item.id,
          x: item.x,
          $width: item.$width,
          $hasWorkSurface: item.$hasWorkSurface,
        })
        continue
      }

      // Check if next cabinet is close. If not, stop moving items.
      const newX = $wall.$direction === Model.WallDirection.left ? nextItemOffset - item.$width : nextItemOffset
      const xDiff = Math.abs(newX - item.x)
      if (xDiff > $oldEntity.$width + $oldEntity.$room.$minGapForOpenCabinet) {
        // This item and all following won't move - keep them in current positions
        newBottomItemPositions.push({
          id: item.id,
          x: item.x,
          $width: item.$width,
          $hasWorkSurface: item.$hasWorkSurface,
        })
        break
      }

      bottomItemUpdates.push(
        configuratorInterface.updates.DomainEntityModification({
          entityId: item.id,
          stateDelta: {
            x: newX,
          },
          updateTag: 'fillEmptySpaceAfterWallItemDelete',
        })
      )

      // Track the new position
      newBottomItemPositions.push({
        id: item.id,
        x: newX,
        $width: item.$width,
        $hasWorkSurface: item.$hasWorkSurface,
      })

      nextItemOffset += isLeftDirection ? -item.$width : item.$width
    }

    // Generate countertop repositioning updates based on new bottom item positions
    const stackProjections = new StackProjections(ctx.domainContext.componentReader)
    const countertopUpdates = generateCountertopRepositioningUpdates(
      $wall,
      newBottomItemPositions,
      stackProjections,
      newState
    )

    return [...bottomItemUpdates, ...countertopUpdates]
  })
  .build('Fill empty space after wall item deleted')
